import React from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Button, HStack, useColorModeValue, Badge, Text, Flex, Icon, VStack, Stack, Box, Heading
} from '@chakra-ui/react';
import { CheckIcon, WarningIcon, TimeIcon, QuestionIcon } from '@chakra-ui/icons';
import { FiMessageCircle, FiUser } from 'react-icons/fi';

/**
 * Detail Item Component for Modal
 */
const DetailItem = ({ label, value }) => (
  <Box mb={2}>
    <Text fontWeight="bold" fontSize="sm" color="gray.500">
      {label}
    </Text>
    <Text>{value}</Text>
  </Box>
);

/**
 * Status Badge Component
 */
const StatusBadge = ({ isCompleted, isInProgress, isPendingConfirmation }) => {
  let colorScheme, status, icon;

  if (isCompleted) {
    colorScheme = 'green';
    status = 'Concluído';
    icon = CheckIcon;
  } else if (isPendingConfirmation) {
    colorScheme = 'orange';
    status = 'Aguardando Confirmação';
    icon = QuestionIcon;
  } else if (isInProgress) {
    colorScheme = 'yellow';
    status = 'Em Andamento';
    icon = TimeIcon;
  } else {
    colorScheme = 'red';
    status = 'Aguardando';
    icon = WarningIcon;
  }

  return (
    <Badge colorScheme={colorScheme} px={2} py={1} borderRadius="full">
      <Flex align="center">
        <Icon as={icon} mr={1} boxSize="10px" />
        <Text>{status}</Text>
      </Flex>
    </Badge>
  );
};

/**
 * Message Bubble Component
 */
const MessageBubble = ({ message, workerName }) => {
  const isWorker = message.senderName === workerName;
  const bubbleBg = useColorModeValue(
    isWorker ? 'purple.50' : 'blue.50',
    isWorker ? 'purple.900' : 'blue.900'
  );
  const borderColor = useColorModeValue(
    isWorker ? 'purple.200' : 'blue.200',
    isWorker ? 'purple.700' : 'blue.700'
  );
  const headerColor = useColorModeValue(
    isWorker ? 'purple.700' : 'blue.700',
    isWorker ? 'purple.300' : 'blue.300'
  );
  
  return (
    <Flex 
      direction="column"
      mb={3}
      alignSelf={isWorker ? "flex-end" : "flex-start"}
      maxW="80%"
    >
      <Flex align="center" mb={1} gap={2}>
        <Text fontWeight="bold" fontSize="sm" color={headerColor}>
          {message.senderName || 'Usuário'}
        </Text>
      </Flex>
      
      <Box
        bg={bubbleBg}
        p={3}
        borderRadius="lg"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Text>{message.content || 'Mensagem indisponível'}</Text>
        <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
          {message.timestamp ? new Date(message.timestamp).toLocaleString() : 'Horário indisponível'}
        </Text>
      </Box>
    </Flex>
  );
};

/**
 * WorkerCancellationDetailsModal Component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.cancellationData - Data for the cancellation request
 * @param {Function} props.onActionTaken - Function to handle action taken
 * @param {Function} props.onOpenChat - Function to open the chat
 */
const WorkerCancellationDetailsModal = ({ isOpen, onClose, cancellationData, onActionTaken, onOpenChat }) => {
  // Colors
  const modalHeaderBg = useColorModeValue('blue.50', 'blue.900');
  const modalHeaderColor = useColorModeValue('blue.700', 'blue.100');
  const mainBoxBg = useColorModeValue('gray.50', 'gray.700');
  const reasonBoxBg = useColorModeValue('red.50', 'red.900');
  const serviceBoxBg = useColorModeValue('blue.50', 'blue.900');
  const userBoxBg = useColorModeValue('teal.50', 'teal.900');
  const conversationBoxBg = useColorModeValue('purple.50', 'purple.900');
  const filterBorderColor = useColorModeValue('gray.200', 'gray.600');
  const messageBg = useColorModeValue('white', 'gray.800');
  const trackBg = useColorModeValue('gray.100', 'gray.800');
  const thumbBg = useColorModeValue('blue.200', 'blue.700');

  if (!cancellationData) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>No data available</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>No cancellation data available</Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  const {
    _id,
    cancellationOption,
    reason,
    worker,
    user,
    conversation,
    actionTaken,
    inProgress,
    pendingConfirmation,
    createdAt
  } = cancellationData;

  const hasMessages = conversation && conversation.messages && conversation.messages.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="md">
        <ModalHeader 
          bg={modalHeaderBg}
          color={modalHeaderColor}
          borderTopRadius="md"
          py={4}
        >
          <HStack>
            <Text>Detalhes do Cancelamento do Contratado</Text>
            <StatusBadge
              isCompleted={actionTaken}
              isInProgress={inProgress}
              isPendingConfirmation={pendingConfirmation}
            />
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={4}>
          <VStack align="stretch" spacing={4}>
            {/* Main Information */}
            <Box 
              p={4} 
              bg={mainBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="blue.500">
                Informações Principais
              </Heading>
              <Stack spacing={3}>
                <Flex flexWrap="wrap" justify="space-between">
                  <DetailItem label="ID" value={_id || 'N/A'} />
                  <DetailItem 
                    label="Data de Criação" 
                    value={
                      createdAt
                        ? new Date(createdAt).toLocaleString()
                        : 'N/A'
                    } 
                  />
                </Flex>
              </Stack>
            </Box>
            
            {/* Cancellation Reason Box */}
            <Box 
              p={4} 
              bg={reasonBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="red.500">
                Detalhes do Cancelamento
              </Heading>
              <DetailItem label="Opção de Cancelamento" value={cancellationOption || 'N/A'} />
              <DetailItem label="Razão" value={reason || 'N/A'} />
            </Box>
            
            {/* Worker Information */}
            <Box 
              p={4} 
              bg={serviceBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="blue.500">
                Informações do Contratado
              </Heading>
              <Stack spacing={3}>
                <DetailItem 
                  label="Nome" 
                  value={worker?.name || 'N/A'} 
                />
                <Flex flexWrap="wrap" justify="space-between">
                  <DetailItem 
                    label="Email" 
                    value={worker?.email || 'N/A'} 
                  />
                  <DetailItem 
                    label="Telefone" 
                    value={worker?.phone || 'N/A'} 
                  />
                </Flex>
              </Stack>
            </Box>
            
            {/* User Information */}
            <Box 
              p={4} 
              bg={userBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="teal.500">
                Informações do Contratante
              </Heading>
              <Stack spacing={3}>
                <DetailItem 
                  label="Nome" 
                  value={user?.name || 'N/A'} 
                />
                <Flex flexWrap="wrap" justify="space-between">
                  <DetailItem 
                    label="Email" 
                    value={user?.email || 'N/A'} 
                  />
                  <DetailItem 
                    label="Telefone" 
                    value={user?.phone || 'N/A'} 
                  />
                </Flex>
              </Stack>
            </Box>
            
            {/* Conversation Messages (if available) */}
            {hasMessages && (
              <Box 
                p={4} 
                bg={conversationBoxBg} 
                borderRadius="md"
                borderWidth="1px"
                borderColor={filterBorderColor}
              >
                <Heading as="h3" size="sm" mb={3} color="purple.500">
                  Conversas
                </Heading>
                
                <Box
                  maxH="400px"
                  overflowY="auto"
                  display="flex"
                  flexDirection="column"
                  p={3}
                  borderWidth="1px"
                  borderColor={filterBorderColor}
                  borderRadius="md"
                  bg={messageBg}
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: trackBg,
                      borderRadius: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: thumbBg,
                      borderRadius: '8px',
                    },
                  }}
                >
                  {conversation.messages.map((message, index) => (
                    <MessageBubble 
                      key={index} 
                      message={message} 
                      workerName={worker?.name} 
                    />
                  ))}
                </Box>
                
                {conversation?.participants && conversation.participants.length > 0 && (
                  <Box mt={3}>
                    <Text fontWeight="bold" fontSize="sm" color="gray.500" mb={2}>
                      Participantes:
                    </Text>
                    <Stack spacing={2}>
                      {conversation.participants.map((participant, index) => (
                        <Text key={index}>
                          <strong>{participant?.name || 'Usuário'}</strong>
                          {participant?.email && ` (${participant.email})`}
                        </Text>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px" borderColor={filterBorderColor}>
          <HStack spacing={3}>
            {inProgress && !actionTaken && !pendingConfirmation && (
              <Button
                leftIcon={<CheckIcon />}
                colorScheme="green"
                onClick={() => onActionTaken(_id)}
              >
                Finalizar
              </Button>
            )}
            {pendingConfirmation && !actionTaken && (
              <Badge colorScheme="orange" px={3} py={2} borderRadius="md">
                <Flex align="center">
                  <Icon as={QuestionIcon} mr={2} />
                  <Text>Aguardando resposta do trabalhador</Text>
                </Flex>
              </Badge>
            )}
            {user && user._id && (
              <Button
                leftIcon={<FiMessageCircle />}
                colorScheme="teal"
                variant="outline"
                onClick={() => onOpenChat(user._id)}
              >
                Chat com Contratante
              </Button>
            )}
            <Button onClick={onClose} variant="ghost">
              Fechar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WorkerCancellationDetailsModal;